// --- Dependencies -------------------------------------------------------------------------------
import type { ReactNode } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    icon: string;
    title: string;
    subtitle: string;
    badge?: string;
    children: ReactNode;
    /** Omit the whole save block for sections that apply instantly or are read-only. */
    actions?: {
        dirty: boolean;
        saving: boolean;
        saved: boolean;
        error: string | null;
        saveLabel: string;
        savingLabel: string;
        cancelLabel: string;
        savedLabel: string;
        onSave: () => void;
        onCancel: () => void;
    };
};

// --- Logic --------------------------------------------------------------------------------------

export default function SettingsCard({ icon, title, subtitle, badge, children, actions }: Props) {
    return (
        <section className="settings-card">
            <header className="settings-card-header">
                <span className="settings-card-icon">
                    <img src={icon} alt="" width={18} height={18} />
                </span>
                <div className="settings-card-heading">
                    <p className="settings-card-title">{title}</p>
                    <p className="settings-card-subtitle">{subtitle}</p>
                </div>
                {badge && <span className="settings-card-badge">{badge}</span>}
                {/* A dot beats a permanently disabled button for telling someone work is unsaved. */}
                {actions?.dirty && <span className="settings-card-dot" aria-hidden="true" />}
            </header>

            <div className="settings-card-body">{children}</div>

            {actions && (
                <div className="settings-card-footer" data-open={actions.dirty || actions.saving || Boolean(actions.error) || actions.saved}>
                    <div className="settings-card-footer-inner">
                        {/* A save that landed leaves nothing to act on, so the buttons give way to the confirmation. */}
                        {actions.saved && !actions.dirty ? (
                            <p className="settings-card-saved">
                                <img src="/icons/check-icon.svg" alt="" width={13} height={13} />
                                {actions.savedLabel}
                            </p>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="settings-card-save"
                                    onClick={actions.onSave}
                                    disabled={actions.saving || !actions.dirty}
                                    aria-label={actions.saving ? actions.savingLabel : undefined}
                                >
                                    {actions.saving ? <span className="settings-card-spinner" /> : actions.saveLabel}
                                </button>
                                <button type="button" className="settings-card-cancel" onClick={actions.onCancel} disabled={actions.saving}>
                                    {actions.cancelLabel}
                                </button>
                                {actions.error && <p className="settings-card-error">{actions.error}</p>}
                            </>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
